import React, { useState, useRef, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowRight,
  Brain,
  Code2,
  Link as LinkIcon,
  Palette,
  Gamepad2,
  Wifi,
  Users,
  Headphones,
  Settings,
  Share2,
  Smartphone,
  Zap,
  LineChart,
  CheckCircle,
  Globe,
  ChevronRight,
  Briefcase,
  DollarSign,
  Clock,
  ChevronDown,
  HelpCircle,
  Server,
  Database,
  Cloud,
  Plug,
  TestTube,
  Layers,
  Building,
  FlaskConical,
  Rocket,
  Bot,
  MessageSquare,
  Package,
  Target,
  Cpu,
  Network,
  Sparkles,
  Building2,
} from "lucide-react";
import {
  PRODUCT_ENGINEERING_SERVICES,
  SERVICE_BUNDLES,
  INDIVIDUAL_SERVICES,
  CASE_STUDIES,
  INDUSTRIES,
  INDUSTRY_ICONS,
} from "../data/mock";
import { TEAM_IMAGES } from "@/data/teamImages";
// Integration logos (src/assets/technology_logo)
import aadhaarLogo from "../assets/technology_logo/aadhar.png";
import amazonCognitoLogo from "../assets/technology_logo/amazon.png";
import appPurchaseLogo from "../assets/technology_logo/app_purchase.png";
import applePayLogo from "../assets/technology_logo/applepay.png";
import auth0Logo from "../assets/technology_logo/auth.png";
import awsLogo from "../assets/technology_logo/Amazon-Web-Services-AWS-Symbol.png";
import bluetoothLogo from "../assets/technology_logo/bluetooth.png";
import braintreeLogo from "../assets/technology_logo/braintree.png";
import bugsnagLogo from "../assets/technology_logo/bugsnag.png";
import connexpayLogo from "../assets/technology_logo/conne.png";
import experianLogo from "../assets/technology_logo/experian.png";
import firebaseLogo from "../assets/technology_logo/firebase.png";
import googleMapsLogo from "../assets/technology_logo/google_maps_platform.png";
import googlePayLogo from "../assets/technology_logo/googlepay.png";
import googleWalletLogo from "../assets/technology_logo/google_wallett.png";
import jwPlayerLogo from "../assets/technology_logo/jw_player.png";
import kountLogo from "../assets/technology_logo/kount.png";
import lottieFilesLogo from "../assets/technology_logo/lattie_files.png";
import mixpanelLogo from "../assets/technology_logo/mix_pannel.png";
import nfcLogo from "../assets/technology_logo/nfc.png";
import openWeatherLogo from "../assets/technology_logo/open_whether.png";
import paypalLogo from "../assets/technology_logo/paypal.png";
import plaidLogo from "../assets/technology_logo/plaid.png";
import scanditLogo from "../assets/technology_logo/scandit.png";
import smartlookLogo from "../assets/technology_logo/smart_talk.png";
import squareLogo from "../assets/technology_logo/squre.png";
import stripeLogo from "../assets/technology_logo/stripe.png";
import uxcamLogo from "../assets/technology_logo/uxcam.png";
import walletLogo from "../assets/technology_logo/wallet.png";

const iconMap = {
  Brain,
  Code2,
  Link: LinkIcon,
  Palette,
  Gamepad2,
  Wifi,
  Users,
  Headphones,
  Settings,
  Share2,
  Smartphone,
  Zap,
  LineChart,
  CheckCircle,
  Globe,
};

// Service Bundles with images
const SERVICE_BUNDLES_DATA = [
  {
    slug: "ai-studio",
    title: "AI Studio",
    description:
      "Rapid AI prototyping and development environment for building intelligent solutions. From concept to deployment in weeks, not months.",
    image:
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&q=80",
    path: "/what-we-do/ai-studio",
    color: "#8b5cf6",
  },
  {
    slug: "dedicated-capability-centre",
    title: "Dedicated Capability Centre",
    description:
      "Your extended technology team. Dedicated resources working exclusively on your projects with deep domain expertise.",
    image:
      "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&q=80",
    path: "/what-we-do/dedicated-capability-centre",
    color: "#06b6d4",
  },
  {
    slug: "startup-lab",
    title: "Startup Lab",
    description:
      "Accelerate your startup journey from idea to MVP. We help founders validate, build, and launch their products faster.",
    image:
      "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=400&q=80",
    path: "/what-we-do/startup-lab",
    color: "#10b981",
  },
  {
    slug: "product-engineering",
    title: "Product Engineering",
    description:
      "End-to-end product development from ideation to scale. We build robust, scalable products that users love.",
    image:
      "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=400&q=80",
    path: "/what-we-do/product-engineering",
    color: "#f59e0b",
  },
];

// Top 4 Individual Services
const TOP_INDIVIDUAL_SERVICES = [
  {
    slug: "mobile-app-development",
    title: "Mobile App Development",
    shortDesc: "Native and cross-platform mobile apps for iOS and Android",
    icon: Smartphone,
    color: "#ec4899",
    path: "/what-we-do/mobile-app-development",
  },
  {
    slug: "web-app-development",
    title: "Web App Development",
    shortDesc:
      "Modern, responsive web applications with cutting-edge technologies",
    icon: Globe,
    color: "#3b82f6",
    path: "/what-we-do/web-app-development",
  },
  {
    slug: "api-development",
    title: "API Development",
    shortDesc: "Robust APIs and microservices for seamless integrations",
    icon: Plug,
    color: "#10b981",
    path: "/what-we-do/api-development",
  },
  {
    slug: "iot-app-development",
    title: "IoT App Development",
    shortDesc: "Connected device solutions and smart IoT platforms",
    icon: Wifi,
    color: "#f59e0b",
    path: "/what-we-do/iot-app-development",
  },
];

// AI Services Cards (from AI landing page)
const AI_SERVICE_CARDS = [
  {
    slug: "ai-consulting",
    title: "AI Consulting",
    description:
      "Strategic AI roadmaps and implementation guidance to transform your business.",
    icon: Target,
    color: "#8b5cf6",
    path: "/ai/ai-consulting",
  },
  {
    slug: "agentic-ai",
    title: "Agentic AI",
    description:
      "Autonomous AI agents that reason, plan, and execute complex tasks.",
    icon: Bot,
    color: "#06b6d4",
    path: "/ai/agentic-ai",
  },
  {
    slug: "ai-products",
    title: "AI Products",
    description:
      "End-to-end AI product development from ideation to deployment.",
    icon: Package,
    color: "#10b981",
    path: "/ai/ai-products",
  },
  {
    slug: "smart-ai-chatbot",
    title: "Smart AI Chatbot",
    description:
      "Intelligent conversational AI for customer engagement and support.",
    icon: MessageSquare,
    color: "#3b82f6",
    path: "/ai/smart-ai-chatbot",
  },
];

// Technology data
const TECHNOLOGIES = {
  mobile: {
    title: "Mobile",
    icon: Smartphone,
    items: [
      {
        name: "React Native",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg",
      },
      {
        name: "iOS Swift",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/swift/swift-original.svg",
      },
      {
        name: "Android Kotlin",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kotlin/kotlin-original.svg",
      },
      {
        name: "Flutter",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flutter/flutter-original.svg",
      },
      {
        name: "Java",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg",
      },
    ],
  },
  frontend: {
    title: "Frontend",
    icon: Globe,
    items: [
      {
        name: "React JS",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg",
      },
      {
        name: "Vue.js",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vuejs/vuejs-original.svg",
      },
      {
        name: "Angular",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/angularjs/angularjs-original.svg",
      },
      {
        name: "Next.js",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg",
      },
      {
        name: "TypeScript",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg",
      },
      {
        name: "Tailwind",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tailwindcss/tailwindcss-original.svg",
      },
    ],
  },
  backend: {
    title: "Backend",
    icon: Server,
    items: [
      {
        name: "Python",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
      },
      {
        name: "Node.js",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg",
      },
      {
        name: "PHP",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/php/php-original.svg",
      },
      {
        name: "Laravel",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/laravel/laravel-original.svg",
      },
      {
        name: "Django",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/django/django-plain.svg",
      },
      {
        name: ".NET",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/dotnetcore/dotnetcore-original.svg",
      },
        {
        name: "Java",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg",
      },
      {
        name: "Ruby on Rails",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/rails/rails-plain.svg",
      },
    ],
  },
  database: {
    title: "Database",
    icon: Database,
    items: [
      {
        name: "PostgreSQL",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg",
      },
      {
        name: "MongoDB",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg",
      },
      {
        name: "MySQL",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg",
      },
      {
        name: "Redis",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg",
      },
      {
        name: "Firebase",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg",
      },
      {
        name: "Elasticsearch",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/elasticsearch/elasticsearch-original.svg",
      },
    ],
  },
  ai: {
    title: "AI & ML",
    icon: Brain,
    items: [
      {
        name: "TensorFlow",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tensorflow/tensorflow-original.svg",
      },
      {
        name: "PyTorch",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/pytorch/pytorch-original.svg",
      },
      {
        name: "OpenAI",
        logo: TEAM_IMAGES.openai,
      },
      {
        name: "Scikit-learn",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/scikitlearn/scikitlearn-original.svg",
      },
      {
        name: "Hugging Face",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
      },
      {
        name: "LangChain",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
      },
    ],
  },
  cloud: {
    title: "Cloud & DevOps",
    icon: Cloud,
    items: [
      {
        name: "AWS",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg",
      },
      {
        name: "Google Cloud",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/googlecloud/googlecloud-original.svg",
      },
      {
        name: "Azure",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg",
      },
      {
        name: "Docker",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg",
      },
      {
        name: "Kubernetes",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg",
      },
      {
        name: "GitHub Actions",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg",
      },
    ],
  },
  integrations: {
    title: "Integrations",
    icon: Plug,
    items: [
      // Payments
      { name: "Stripe", logo: stripeLogo },
      { name: "PayPal", logo: paypalLogo },
      { name: "Braintree", logo: braintreeLogo },
      { name: "Square", logo: squareLogo },
      { name: "ConnexPay", logo: connexpayLogo },
      { name: "In-App Purchase", logo: appPurchaseLogo },
      { name: "Google Pay", logo: googlePayLogo },
      { name: "Apple Pay", logo: applePayLogo },
      { name: "Google Wallet", logo: googleWalletLogo },
      { name: "Wallet", logo: walletLogo },

      // Identity, risk & data
      { name: "Auth0", logo: auth0Logo },
      { name: "Amazon Cognito", logo: amazonCognitoLogo },
      { name: "Aadhaar", logo: aadhaarLogo },
      { name: "Plaid", logo: plaidLogo },
      { name: "Experian", logo: experianLogo },
      { name: "Kount", logo: kountLogo },

      // Platform & cloud
      { name: "AWS", logo: awsLogo },
      { name: "Firebase", logo: firebaseLogo },
      { name: "Google Maps Platform", logo: googleMapsLogo },
      { name: "OpenWeather", logo: openWeatherLogo },

      // Media & device
      { name: "JW Player", logo: jwPlayerLogo },
      { name: "LottieFiles", logo: lottieFilesLogo },
      { name: "Scandit", logo: scanditLogo },
      { name: "NFC", logo: nfcLogo },
      { name: "Bluetooth Low Energy", logo: bluetoothLogo },

      // Analytics & monitoring
      { name: "Mixpanel", logo: mixpanelLogo },
      { name: "UXCam", logo: uxcamLogo },
      { name: "Bugsnag", logo: bugsnagLogo },
      { name: "Smartlook", logo: smartlookLogo },

      // Protocols
      {
        name: "Socket.io",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/socketio/socketio-original.svg",
      },
      {
        name: "GraphQL",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/graphql/graphql-plain.svg",
      },
      {
        name: "REST APIs",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg",
      },
    ],
  },
  qa: {
    title: "QA & Testing",
    icon: TestTube,
    items: [
      {
        name: "Selenium",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/selenium/selenium-original.svg",
      },
      {
        name: "Cypress",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cypressio/cypressio-original.svg",
      },
      {
        name: "Jest",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jest/jest-plain.svg",
      },
      {
        name: "Mocha",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mocha/mocha-plain.svg",
      },
      {
        name: "Postman",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postman/postman-original.svg",
      },
      {
        name: "Pytest",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/pytest/pytest-original.svg",
      },
    ],
  },
};

// FAQ Data
const FAQ_DATA = [
  {
    question: "What services does Infotech.Works offer?",
    answer:
      "We offer comprehensive technology services including Mobile App Development, Web Application Development, AI & Machine Learning Solutions, IoT Development, Cloud Solutions, UI/UX Design, Quality Assurance, and DevOps services. Our expertise spans across 20+ industries.",
  },
  {
    question: "How do you ensure project delivery within timeline?",
    answer:
      "We follow Agile methodology with 2-week sprint cycles, daily standups, and regular client demos. Our project managers use proven tools for tracking progress, and we maintain transparent communication through weekly status reports.",
  },
  {
    question: "What engagement models do you offer?",
    answer:
      "We offer three flexible engagement models: Fixed Price for well-defined projects, Fixed Team (Dedicated Team) for long-term partnerships, and Time & Material for projects with evolving requirements. We help you choose the best model based on your needs.",
  },
  {
    question: "How do you handle communication and project updates?",
    answer:
      "We assign a dedicated Project Manager as your single point of contact. Communication happens through your preferred channels (Slack, Teams, Email). You'll receive daily updates during active development and weekly progress reports.",
  },
  {
    question: "What technologies do you specialize in?",
    answer:
      "Our team is proficient in React Native, Swift, Kotlin, Flutter for mobile; React, Angular, Vue.js for frontend; Node.js, Python, PHP, .NET for backend; AWS, Azure, Google Cloud for infrastructure; and TensorFlow, PyTorch for AI/ML.",
  },
  {
    question: "Can you scale the team based on project requirements?",
    answer:
      "Absolutely. Our flexible resource model allows us to scale teams up or down based on project phases. We can adjust team composition within 1-2 weeks while maintaining project continuity.",
  },
  {
    question: "What is your quality assurance process?",
    answer:
      "We follow a comprehensive QA process including unit testing, integration testing, system testing, and UAT. Our QA team works alongside developers from day one, implementing automated testing where applicable.",
  },
  {
    question: "How do you handle data security?",
    answer:
      "Security is paramount. We sign NDAs before project kickoff, implement role-based access controls, use encrypted communication channels, and follow secure coding practices (OWASP guidelines).",
  },
  {
    question: "What happens after the project is delivered?",
    answer:
      "We provide comprehensive post-launch support including a warranty period for bug fixes, knowledge transfer sessions, detailed documentation, and optional maintenance contracts.",
  },
  {
    question: "How do I get started with a new project?",
    answer:
      "Getting started is easy! Contact us through our website or email. We'll schedule a discovery call to understand your requirements, followed by a detailed proposal with scope, timeline, and cost estimates.",
  },
];

// FAQ Component
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-flex items-center px-4 py-2 bg-amber-50 text-amber-600 rounded-full text-sm font-semibold mb-4">
            <HelpCircle className="h-4 w-4 mr-2" />
            Got Questions?
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Find answers to common questions about our services and process
          </p>
        </div>

        <div className="space-y-4">
          {FAQ_DATA.map((faq, index) => (
            <div
              key={index}
              className={`border rounded-2xl overflow-hidden transition-all duration-300 ${
                openIndex === index
                  ? "shadow-lg border-[#b32126]/30 bg-white"
                  : "border-gray-200 bg-gray-50 hover:border-gray-300"
              }`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left"
              >
                <span
                  className={`font-semibold pr-4 ${
                    openIndex === index ? "text-[#b32126]" : "text-gray-900"
                  }`}
                >
                  {faq.question}
                </span>
                <div
                  className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                    openIndex === index
                      ? "bg-[#b32126]/10 rotate-180"
                      : "bg-gray-200"
                  }`}
                >
                  <ChevronDown
                    className={`h-5 w-5 ${
                      openIndex === index ? "text-[#b32126]" : "text-gray-500"
                    }`}
                  />
                </div>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? "max-h-96" : "max-h-0"
                }`}
              >
                <div className="px-6 pb-5 text-gray-600 leading-relaxed">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ServicesPage = () => {
  const { slug } = useParams();
  const [activeTab, setActiveTab] = useState("mobile");
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);

  // Get case studies
  const displayCaseStudies = CASE_STUDIES.slice(0, 3);

  // Mouse move effect for hero
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setMousePosition({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
    };

    const heroElement = heroRef.current;
    if (heroElement) {
      heroElement.addEventListener("mousemove", handleMouseMove);
    }

    return () => {
      if (heroElement) {
        heroElement.removeEventListener("mousemove", handleMouseMove);
      }
    };
  }, []);

  // If slug is provided, show individual service page
  if (slug) {
    const service = PRODUCT_ENGINEERING_SERVICES.find((s) => s.slug === slug);
    if (!service) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Service Not Found
            </h1>
            <Link to="/what-we-do" className="text-[#b32126] hover:underline">
              Back
            </Link>
          </div>
        </div>
      );
    }

    const IconComponent = iconMap[service.icon] || Code2;

    return (
      <div>
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <Link
                to="/what-we-do"
                className="text-[#b32126] hover:text-[#d42a30] font-medium mb-4 inline-block"
              >
                ← Back
              </Link>
              <div className="w-16 h-16 bg-[#b32126] rounded-2xl flex items-center justify-center mb-6">
                <IconComponent className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {service.title}
              </h1>
              <p className="text-xl text-gray-300">{service.shortDesc}</p>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-12">
              <div className="lg:col-span-2">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Overview
                </h2>
                <p className="text-gray-600 text-lg leading-relaxed mb-8">
                  {service.description}
                </p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  What We Offer
                </h3>
                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  {service.features.map((feature, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg"
                    >
                      <CheckCircle className="h-5 w-5 text-[#b32126] flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="bg-gray-50 rounded-2xl p-8 sticky top-24">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">
                    Start Your Project
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Ready to build something amazing? Let&apos;s discuss your{" "}
                    {service.title.toLowerCase()} needs.
                  </p>
                  <Link
                    to="/contact"
                    className="w-full inline-flex items-center justify-center px-6 py-3 bg-[#b32126] text-white font-semibold rounded-lg hover:bg-[#8f1a1f] transition-colors"
                  >
                    Get Quote
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">
              Related Services
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {PRODUCT_ENGINEERING_SERVICES.filter((s) => s.slug !== slug)
                .slice(0, 3)
                .map((s) => {
                  const Icon = iconMap[s.icon] || Code2;
                  return (
                    <Link
                      key={s.slug}
                      to={`/what-we-do/${s.slug}`}
                      className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all border border-gray-100 group"
                    >
                      <Icon className="h-8 w-8 text-[#b32126] mb-4" />
                      <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-[#b32126] transition-colors">
                        {s.title}
                      </h3>
                      <p className="text-gray-600 text-sm">{s.shortDesc}</p>
                    </Link>
                  );
                })}
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Main Services listing page
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Same as AI Landing Page */}
      <section
        ref={heroRef}
        className="relative min-h-[500px] flex items-center overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
        }}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Floating Icons */}
          {[Layers, Code2, Smartphone, Globe, Server, Zap].map((Icon, i) => (
            <div
              key={i}
              className="absolute animate-pulse"
              style={{
                left: `${15 + i * 15}%`,
                top: `${20 + (i % 3) * 25}%`,
                animationDelay: `${i * 0.5}s`,
                animationDuration: `${3 + i * 0.5}s`,
              }}
            >
              <Icon className="h-8 w-8 text-white/10" />
            </div>
          ))}

          {/* Grid Pattern */}
          <svg className="absolute inset-0 w-full h-full opacity-10">
            <defs>
              <pattern
                id="services-grid"
                width="60"
                height="60"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 60 0 L 0 0 0 60"
                  fill="none"
                  stroke="white"
                  strokeWidth="0.5"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#services-grid)" />
          </svg>

          {/* Mouse follower glow */}
          <div
            className="absolute w-96 h-96 rounded-full opacity-20 blur-3xl"
            style={{
              background:
                "radial-gradient(circle, #b32126 0%, transparent 70%)",
              left: mousePosition.x - 192,
              top: mousePosition.y - 192,
              transition: "left 0.3s ease-out, top 0.3s ease-out",
            }}
          />

          {/* Decorative circles */}
          <div
            className="absolute top-20 right-20 w-64 h-64 rounded-full border border-white/10 animate-spin"
            style={{ animationDuration: "20s" }}
          />
          <div
            className="absolute bottom-20 left-20 w-48 h-48 rounded-full border border-[#b32126]/20 animate-spin"
            style={{ animationDuration: "15s", animationDirection: "reverse" }}
          />
        </div>

        {/* Hero Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium mb-6 text-red-300">
                <Layers className="h-4 w-4 mr-2" />
                Engineering Excellence
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                What We
                <span className="block text-[#b32126]">Do</span>
              </h1>

              <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-lg">
                From MVP to enterprise scale — we engineer AI, mobile, and web
                products that perform, scale, and deliver measurable business
                results.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center px-8 py-4 text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  style={{
                    background:
                      "linear-gradient(135deg, #d64449 0%, #b32126 50%, #8f1a1f 100%)",
                  }}
                >
                  Start Your Project
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/resources/case-studies"
                  className="inline-flex items-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-all backdrop-blur-sm"
                >
                  View Case Studies
                </Link>
              </div>
            </div>

            {/* Stats Card */}
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[#b32126]/30 to-blue-500/30 rounded-3xl blur-2xl" />
                <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center p-4">
                      <div className="text-4xl font-bold text-white mb-1">
                        250+
                      </div>
                      <div className="text-gray-300 text-sm">
                        Projects Delivered
                      </div>
                    </div>
                    <div className="text-center p-4">
                      <div className="text-4xl font-bold text-white mb-1">
                        175+
                      </div>
                      <div className="text-gray-300 text-sm">Happy Clients</div>
                    </div>
                    <div className="text-center p-4">
                      <div className="text-4xl font-bold text-white mb-1">
                        20+
                      </div>
                      <div className="text-gray-300 text-sm">Industries</div>
                    </div>
                    <div className="text-center p-4">
                      <div className="text-4xl font-bold text-white mb-1">
                        99%
                      </div>
                      <div className="text-gray-300 text-sm">Satisfaction</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Bundles Section - 4 Rows */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-flex items-center px-4 py-2 bg-purple-50 text-purple-600 rounded-full text-sm font-semibold mb-4">
              <Briefcase className="h-4 w-4 mr-2" />
              Service Bundles
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Service Packages
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              End-to-end solutions designed to accelerate your business growth
            </p>
          </div>

          <div className="space-y-6">
            {SERVICE_BUNDLES_DATA.map((bundle, index) => (
              <Link
                key={bundle.slug}
                to={bundle.path}
                className="group flex flex-col md:flex-row items-center gap-6 bg-white p-6 rounded-2xl shadow-sm hover:shadow-xl transition-all border border-gray-100 overflow-hidden"
              >
                {/* Image */}
                <div className="w-full md:w-48 h-40 md:h-32 flex-shrink-0 overflow-hidden rounded-xl">
                  <img
                    src={bundle.image}
                    alt={bundle.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>

                {/* Content */}
                <div className="flex-grow">
                  <div className="flex items-center gap-3 mb-2">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${bundle.color}20` }}
                    >
                      <Layers
                        className="h-5 w-5"
                        style={{ color: bundle.color }}
                      />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#b32126] transition-colors">
                      {bundle.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 mb-3">{bundle.description}</p>
                </div>

                {/* Arrow */}
                <div className="flex-shrink-0">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center group-hover:translate-x-2 transition-transform"
                    style={{ backgroundColor: `${bundle.color}15` }}
                  >
                    <ArrowRight
                      className="h-5 w-5"
                      style={{ color: bundle.color }}
                    />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Individual Services Section - Top 4 Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-600 rounded-full text-sm font-semibold mb-4">
              <Code2 className="h-4 w-4 mr-2" />
              Featured Services
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Specialized Development Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Focused expertise for your specific technology needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TOP_INDIVIDUAL_SERVICES.map((service) => {
              const IconComponent = service.icon;
              return (
                <Link
                  key={service.slug}
                  to={service.path}
                  className="group relative bg-white p-8 rounded-2xl shadow-sm hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden"
                >
                  {/* Hover gradient */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{
                      background: `linear-gradient(135deg, ${service.color}08 0%, ${service.color}05 100%)`,
                    }}
                  />

                  <div className="relative z-10">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg"
                      style={{
                        background: `linear-gradient(135deg, ${service.color} 0%, ${service.color}dd 100%)`,
                      }}
                    >
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-[#b32126] transition-colors">
                      {service.title}
                    </h3>

                    <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                      {service.shortDesc}
                    </p>

                    <span
                      className="inline-flex items-center font-semibold text-sm group-hover:translate-x-2 transition-transform"
                      style={{ color: service.color }}
                    >
                      Learn More <ArrowRight className="ml-2 h-4 w-4" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link
              to="#all-services"
              className="inline-flex items-center px-6 py-3 border-2 border-[#b32126] text-[#b32126] font-semibold rounded-lg hover:bg-[#b32126] hover:text-white transition-colors"
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById("all-services")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              View All Services
              <ChevronDown className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* AI Services Section - From AI Landing Page */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-flex items-center px-4 py-2 bg-red-50 text-[#b32126] rounded-full text-sm font-semibold mb-4">
              <Sparkles className="h-4 w-4 mr-2" />
              Our AI Services
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive AI Solutions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From strategy to deployment, we offer end-to-end AI services to
              transform your business
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {AI_SERVICE_CARDS.map((service) => {
              const IconComponent = service.icon;
              return (
                <Link
                  key={service.slug}
                  to={service.path}
                  className="group relative bg-white p-8 rounded-2xl shadow-sm hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden"
                >
                  {/* Hover gradient */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{
                      background: `linear-gradient(135deg, ${service.color}08 0%, ${service.color}05 100%)`,
                    }}
                  />

                  <div className="relative z-10">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg"
                      style={{
                        background: `linear-gradient(135deg, ${service.color} 0%, ${service.color}dd 100%)`,
                      }}
                    >
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#b32126] transition-colors">
                      {service.title}
                    </h3>

                    <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                      {service.description}
                    </p>

                    <span
                      className="inline-flex items-center font-semibold group-hover:translate-x-2 transition-transform"
                      style={{ color: service.color }}
                    >
                      Explore <ArrowRight className="ml-2 h-4 w-4" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We Do Section - All Services */}
      <section id="all-services" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-flex items-center px-4 py-2 bg-indigo-50 text-indigo-600 rounded-full text-sm font-semibold mb-4">
              Complete Offerings
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              All Services at a Glance
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive service offerings to bring your vision to life
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center mr-3">
                  <Briefcase className="h-5 w-5 text-[#b32126]" />
                </div>
                Service Bundles
              </h3>
              <div className="space-y-3">
                {SERVICE_BUNDLES.map((service) => (
                  <Link
                    key={service.slug}
                    to={`/what-we-do/${service.slug}`}
                    className="flex items-center justify-between p-4 rounded-xl bg-white hover:bg-gray-50 transition-colors group border border-gray-100"
                  >
                    <span className="font-medium text-gray-700 group-hover:text-[#b32126]">
                      {service.title}
                    </span>
                    <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-[#b32126]" />
                  </Link>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center mr-3">
                  <Plug className="h-5 w-5 text-[#b32126]" />
                </div>
                Individual Services
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {INDIVIDUAL_SERVICES.map((service) => (
                  <Link
                    key={service.slug}
                    to={`/what-we-do/${service.slug}`}
                    className="p-3 rounded-lg bg-white hover:bg-gray-50 transition-colors group border border-gray-100"
                  >
                    <span className="text-sm font-medium text-gray-600 group-hover:text-[#b32126]">
                      {service.title}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section - Show first 2 rows (12 items on desktop) */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Industries We Serve
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Deep expertise across {INDUSTRIES.length}+ industries, delivering
              specialized solutions
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {INDUSTRIES.slice(0, 12).map((industry) => {
              const IconComponent = INDUSTRY_ICONS[industry.slug] || Building2;
              return (
                <Link
                  key={industry.slug}
                  to={`/industries/${industry.slug}`}
                  className="group p-4 bg-gray-800 rounded-xl text-center hover:bg-[#b32126] transition-all"
                >
                  <div className="w-10 h-10 mx-auto mb-3 bg-gray-700 rounded-lg flex items-center justify-center group-hover:bg-white/20 transition-colors">
                    <IconComponent className="h-5 w-5 text-gray-400 group-hover:text-white transition-colors" />
                  </div>
                  <span className="text-gray-300 group-hover:text-white text-sm font-medium block">
                    {industry.title}
                  </span>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/industries"
              className="inline-flex items-center text-[#b32126] hover:text-[#d42a30] font-semibold"
            >
              View All Industries
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-flex items-center px-4 py-2 bg-green-50 text-green-600 rounded-full text-sm font-semibold mb-4">
              Proven Results
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              See how we&apos;ve helped businesses achieve their goals
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {displayCaseStudies.map((study, index) => (
              <Link
                key={study.id}
                to={`/resources/case-studies/${study.slug}`}
                className={`group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-gray-100 ${
                  index === 0 ? "ring-2 ring-[#b32126] ring-offset-2" : ""
                }`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 flex gap-2">
                    <span className="px-3 py-1 bg-[#b32126] text-white text-xs font-semibold rounded-full">
                      {study.industry}
                    </span>
                    {index === 0 && (
                      <span className="px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-full">
                        Featured
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-sm text-gray-500 mb-1">
                    {study.clientType}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#b32126] transition-colors">
                    {study.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {study.tagline}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {study.results.slice(0, 3).map((result, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-full font-medium"
                      >
                        {result.value} {result.label}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/resources/case-studies"
              className="inline-flex items-center px-6 py-3 border-2 border-[#b32126] text-[#b32126] font-semibold rounded-lg hover:bg-[#b32126] hover:text-white transition-colors"
            >
              View All Case Studies
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            {/* <span className="inline-flex items-center px-4 py-2 bg-white/10 text-red-300 rounded-full text-sm font-semibold mb-4">
              Tech Stack
            </span> */}
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Technologies We Use
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Industry-leading tools and frameworks to build robust solutions
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {Object.entries(TECHNOLOGIES)
              .sort(([, a], [, b]) => a.title.localeCompare(b.title))
              .map(([key, category]) => {
              const IconComponent = category.icon;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`flex items-center px-5 py-3 rounded-xl font-medium transition-all ${
                    activeTab === key
                      ? "bg-[#b32126] text-white shadow-lg"
                      : "bg-white/10 text-gray-300 hover:bg-white/20"
                  }`}
                >
                  <IconComponent className="h-5 w-5 mr-2" />
                  {category.title}
                </button>
              );
            })}
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
              {TECHNOLOGIES[activeTab].items.map((tech, index) => (
                <div
                  key={tech.name}
                  className="group flex flex-col items-center p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-all"
                >
                  <div className="w-24 h-20 flex items-center justify-center mb-3 bg-white rounded-xl p-3 group-hover:scale-110 transition-transform shadow-lg">
                    <img
                      src={tech.logo}
                      alt={tech.name}
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </div>
                  <span className="text-white text-sm font-medium text-center">
                    {tech.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Engagement Models */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-600 rounded-full text-sm font-semibold mb-4">
              Flexible Partnership
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Engagement Models
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the partnership model that best fits your project needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Link
              to="/what-we-do/engagement-model#fixed-price"
              className="group relative bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all border border-gray-100 hover:border-emerald-200 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500" />
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <DollarSign className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-emerald-600 transition-colors">
                  Fixed Price
                </h3>
                <p className="text-gray-600 mb-4">
                  Predictable budgets with well-defined scope. Ideal for
                  projects with clear requirements.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-sm text-gray-500">
                    <ChevronRight className="h-4 w-4 text-emerald-500 mr-2" />
                    Budget certainty upfront
                  </li>
                  <li className="flex items-center text-sm text-gray-500">
                    <ChevronRight className="h-4 w-4 text-emerald-500 mr-2" />
                    Milestone-based delivery
                  </li>
                  <li className="flex items-center text-sm text-gray-500">
                    <ChevronRight className="h-4 w-4 text-emerald-500 mr-2" />
                    Clear deliverables
                  </li>
                </ul>
                <span className="inline-flex items-center text-emerald-600 font-medium group-hover:translate-x-2 transition-transform">
                  Learn More <ArrowRight className="ml-2 h-4 w-4" />
                </span>
              </div>
            </Link>

            <Link
              to="/what-we-do/engagement-model#fixed-team"
              className="group relative bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all border border-gray-100 hover:border-blue-200 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-indigo-500/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500" />
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                  Fixed Team
                </h3>
                <p className="text-gray-600 mb-4">
                  Dedicated team working exclusively on your project. Perfect
                  for long-term partnerships.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-sm text-gray-500">
                    <ChevronRight className="h-4 w-4 text-blue-500 mr-2" />
                    Dedicated resources
                  </li>
                  <li className="flex items-center text-sm text-gray-500">
                    <ChevronRight className="h-4 w-4 text-blue-500 mr-2" />
                    Deep domain expertise
                  </li>
                  <li className="flex items-center text-sm text-gray-500">
                    <ChevronRight className="h-4 w-4 text-blue-500 mr-2" />
                    Seamless integration
                  </li>
                </ul>
                <span className="inline-flex items-center text-blue-600 font-medium group-hover:translate-x-2 transition-transform">
                  Learn More <ArrowRight className="ml-2 h-4 w-4" />
                </span>
              </div>
            </Link>

            <Link
              to="/what-we-do/engagement-model#time-and-material"
              className="group relative bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all border border-gray-100 hover:border-purple-200 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/10 to-violet-500/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500" />
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-purple-600 transition-colors">
                  Time & Material
                </h3>
                <p className="text-gray-600 mb-4">
                  Maximum flexibility for evolving requirements. Pay for actual
                  time and resources used.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-sm text-gray-500">
                    <ChevronRight className="h-4 w-4 text-purple-500 mr-2" />
                    Complete flexibility
                  </li>
                  <li className="flex items-center text-sm text-gray-500">
                    <ChevronRight className="h-4 w-4 text-purple-500 mr-2" />
                    Transparent billing
                  </li>
                  <li className="flex items-center text-sm text-gray-500">
                    <ChevronRight className="h-4 w-4 text-purple-500 mr-2" />
                    Scale as needed
                  </li>
                </ul>
                <span className="inline-flex items-center text-purple-600 font-medium group-hover:translate-x-2 transition-transform">
                  Learn More <ArrowRight className="ml-2 h-4 w-4" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection />

      {/* CTA Section */}
      <section
        className="py-20 relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #1a1a2e 0%, #b32126 50%, #8f1a1f 100%)",
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full">
            <defs>
              <pattern
                id="services-cta-pattern"
                width="40"
                height="40"
                patternUnits="userSpaceOnUse"
              >
                <circle cx="20" cy="20" r="2" fill="white" />
              </pattern>
            </defs>
            <rect
              width="100%"
              height="100%"
              fill="url(#services-cta-pattern)"
            />
          </svg>
        </div>

        {/* Floating icons */}
        <div className="absolute inset-0 overflow-hidden">
          {[Layers, Code2, Smartphone, Globe].map((Icon, i) => (
            <div
              key={i}
              className="absolute animate-pulse opacity-20"
              style={{
                left: `${20 + i * 20}%`,
                top: `${30 + (i % 2) * 40}%`,
                animationDelay: `${i * 0.5}s`,
              }}
            >
              <Icon className="h-12 w-12 text-white" />
            </div>
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Build Something Great?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Let&apos;s turn your ideas into reality with our expert technology
            services
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#b32126] font-semibold rounded-xl hover:bg-gray-100 transition-colors shadow-lg"
            >
              Start Your Project
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/resources/case-studies"
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-xl hover:bg-white/10 transition-colors"
            >
              View Case Studies
            </Link>
          </div>
        </div>
      </section>

      <style>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default ServicesPage;
